import { recoveryText } from '@/lib/startRecoveryCopy';
import { resolveIdleGraceMinutes, IDLE_RATE_MAX_CENTS_PER_MIN } from '@evi/shared';
import { useState, useMemo, useEffect, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useStreamAwareInterval } from '@/store/sessionStream';
import { ACTIVE_SESSION_KEY, useActiveSession } from '@/hooks/useActiveSession';
import {
  ArrowLeft, Zap, Clock, CreditCard, AlertTriangle, Loader2, ChevronRight,
  WifiOff, MapPin, Lock,
} from 'lucide-react';
import {
  fetchPublicCharger, fetchPublicLocation, fetchPublicTariff,
  fetchPaymentMethods, startDriverCharger,
  fetchStartPreview, setPaymentSource, fetchCashWallet,
  type PublicTariff, type PublicCharger, type DriverStartError, type StartPreviewResponse,
} from '@/lib/api';
import {
  StickyCTA, PhotoHero, StatCard, StatusPill, type PillTone,
} from '@/components/primitives';
import { viewForConnectorStatus, type ConnectorStatusView } from '@/lib/connectorStatus';
import { toast } from '@/store/toast';
import { useAuthStore } from '@/store/auth';
import { useEvieChatStore } from '@/store/evieChat';
import { useCurrentMode } from '@/hooks/useCurrentMode';
import {
  resolveChargerAccessibility, modeMismatchTarget,
} from '@/lib/mapChargerAccessibility';
import { type SessionAccessReason } from '@evi/shared';
import { PaymentSelector } from './PaymentSelector';
import { walletOptionView } from './walletOptionView';
import { startCredit, startPaymentSource } from './creditDefault';
import { apiMayStartOtherPlug, startedPlugNotice, startedChargerNotice, startQueuedSeconds } from './startedPlugNotice';
import { cashWalletQueryKey } from '@/lib/cashWalletKey';
import { defaultCardSummary } from '@/lib/defaultCard';
import NamedIcon from '@/components/ui/NamedIcon';
import { idleDisclosure } from '@/lib/idleDisclosure';
import { renderInline } from '@evi/ui';
import { DriverAssistFab } from '@/components/DriverAssistSheet';
import { ExitOverlayContext } from '@/components/AppShell';
import { weekdayShort } from '@/lib/time-format';
import { formatMoneyCompact, formatMoneyExplicit } from '@/lib/money';
import { isPriceNotSet } from '@/lib/guestPricing';
import { isExpiredStartCard, isReaddStartCard } from '@/lib/expiredStartCard';
import { expiredCardBlocksStart, startCardExpired } from '@/lib/startCardExpiry';
import { preSessionBackPath } from '@/lib/stallLabels';
import { isPrivateChargerError, isPrivateLocked } from '@/lib/privateCharger';
import { PrivateChargerNotice } from '@/components/station/PrivateChargerNotice';
import styles from './PreSessionPage.module.css';

// Branded gradient tile used as the final fallback when the location
// has no operator-supplied photo_url. Same data URI as StationDetailPage
// so the two screens read as one product. Street View tier lands with
// the queued StationHero ticket.
const HERO_PLACEHOLDER =
  "data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='320' viewBox='0 0 800 320'%3E%3Cdefs%3E%3ClinearGradient id='g' x1='0' y1='0' x2='1' y2='1'%3E%3Cstop offset='0%25' stop-color='%2321c163' stop-opacity='0.4'/%3E%3Cstop offset='100%25' stop-color='%230a0a0a' stop-opacity='0.9'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='800' height='320' fill='url(%23g)'/%3E%3C/svg%3E";

// Hero-pill class per StatusPill tone, for the multi-plug hero pill that
// shows the PICKED plug's own status (Charging / Faulted / Reserved…)
// rather than the charger-level "Available". The three legacy classes
// keep their hardcoded hex (they predate the tone tokens and single-
// plug stalls still render them byte-for-byte); the four new ones lean
// on the theme tokens the same way StatusPill's soft variant does.
// `string | undefined` because CSS-module lookups are index accesses
// under noUncheckedIndexedAccess; the template literal at the use site
// tolerates undefined the same way the page's other class joins do.
const HERO_PILL_TONE_CLASS: Record<PillTone, string | undefined> = {
  good: styles.pillOk,
  warn: styles.pillWarn,
  mute: styles.pillOffline,
  info: styles.pillInfo,
  power: styles.pillPower,
  accent: styles.pillAccent,
  danger: styles.pillDanger,
};

/** The plug the URL asks for, or null. `?plug=` is written by the station
 *  page's stall picker and by this page's own switcher; the stall QR
 *  stickers and older deep links carry none. Anything that is not a
 *  positive integer reads as absent rather than as plug NaN. */
function readPlugFromUrl(): number | null {
  const raw = new URLSearchParams(window.location.search).get('plug');
  const n = raw === null ? Number.NaN : Number(raw);
  return Number.isInteger(n) && n > 0 ? n : null;
}

/** Keep `?plug=` in step with the switcher so a reload (or a share of the
 *  URL bar) lands on the same plug. replaceState, not pushState: a plug
 *  tap is a refinement of this screen, not a place Back should return
 *  to, and App's router only listens for popstate so nothing remounts.
 *  `null` drops the param — used when the server has just told us the
 *  plug in the URL is not on this charger. */
function writePlugToUrl(connectorId: number | null): void {
  const params = new URLSearchParams(window.location.search);
  if (connectorId === null) params.delete('plug');
  else params.set('plug', String(connectorId));
  const search = params.toString();
  window.history.replaceState({}, '', `${window.location.pathname}${search ? `?${search}` : ''}`);
}

/**
 * Status-aware plug default (Warren's ruling, 2026-09-16: "a sticker
 * that always lands on plug 1 even when it is busy is the wrong-pedestal
 * problem in miniature").
 *
 *   1. The wanted plug (URL `?plug=` or the driver's own tap) wins when
 *      it is on this charger AND selectable right now.
 *   2. Otherwise the FIRST selectable plug — the stall QR carries no
 *      plug, so a scan at a dual-port unit with plug 1 charging lands
 *      on the free plug 2 instead of a Start that can only fail.
 *   3. Nothing selectable → the first plug, and the existing gates
 *      (offline / maintenance / server 409) say why.
 *
 * Selectability is `viewForConnectorStatus` — the same verdict the
 * station page's chips use, so the two screens never disagree about
 * which plug a driver may start. A single-plug charger resolves to its
 * only connector under every rule, exactly as before this existed.
 */
function resolveActiveConnector(
  connectors: PublicCharger['connectors'],
  wantedPlug: number | null,
  isOnline: boolean,
): PublicCharger['connectors'][number] | undefined {
  const wanted = wantedPlug !== null
    ? connectors.find((c) => c.connector_id === wantedPlug)
    : undefined;
  if (wanted && viewForConnectorStatus(wanted.status, isOnline).selectable) return wanted;
  return connectors.find((c) => viewForConnectorStatus(c.status, isOnline).selectable)
    ?? connectors[0];
}

/** Localized pill copy for a connector-status view. Same shape as the
 *  station page's helper; the mapping's own `label` is the English
 *  fallback so an unmapped key never renders blank. */
function connectorStatusLabel(t: TFunction, view: ConnectorStatusView): string {
  return t(`connectorStatus.${view.key}`, { defaultValue: view.label });
}

// Radio affordance on each plug chip. Same shape as the station page's
// (which is module-private there); inline colour so the dot follows the
// accent without a per-state class pair.
function RadioDot({ selected, disabled }: { selected: boolean; disabled: boolean }) {
  return (
    <span
      aria-hidden
      className={styles.radioDot}
      style={{
        borderColor: selected ? 'var(--evi-accent)' : 'var(--evi-border-strong)',
        opacity: disabled ? 0.4 : 1,
      }}
    >
      {selected && <span className={styles.radioDotInner} />}
    </span>
  );
}

interface Props {
  locationId: string;
  chargerIdentity: string;
  onNavigate: (path: string) => void;
}

/**
 * Pre-session screen.
 *
 * Driver lands here via (A) station → charger list → tap, or
 * (B) QR scan. Screen gathers location + charger + tariff + payment
 * into one glance then fires RemoteStartTransaction on tap.
 *
 * Live polling: GET /chargers/:id every 10 seconds while the
 * screen is foreground. If the charger flips offline mid-view,
 * the Start button soft-swaps to a disabled state + toast, without
 * navigating the driver away (they might want to wait it out). The
 * server's own CHARGER_OFFLINE 409 is the second line of defense
 * when the driver taps anyway.
 */
export function PreSessionPage({ locationId, chargerIdentity, onNavigate }: Props) {
  const { t, i18n } = useTranslation('preSession');
  const qc = useQueryClient();
  const recoveryId = new URLSearchParams(window.location.search).get('recover');
  const [recoveryConfirmedKey, setRecoveryConfirmedKey] = useState('');
  // A session already in flight (pending plug-in window or active)
  // blocks a second start from this page: the CTA turns into "Session
  // in progress" and takes the driver home, where the live view is.
  // Deep links (/charging-station/:loc?charger=, the stall QR) could
  // otherwise start a second session mid-charge. The finishing tail
  // (StopTransaction landed, plug still in — the BMS-stopped case) does
  // NOT block: that is exactly when a driver restarts to top up.
  // The page's OWN start wins while it is in flight: the server creates
  // the pending shell before the RemoteStart round-trip and the SSE
  // stream lands it in the cache within ~200ms, which would otherwise
  // flip "Sending…" into an enabled "Session in progress" mid-request.
  const currentRow = useActiveSession().data;
  const [starting, setStarting] = useState(false);
  const sessionInFlight = !starting && Boolean(currentRow) && currentRow?.phase !== 'finishing';
  const [startError, setStartError] = useState<DriverStartError | null>(null);
  const [wasOnline, setWasOnline] = useState<boolean | null>(null);
  // Layer 2 of the driver-friction fix: intercept Start tap with an
  // educational modal when no card on file. State is local — modal
  // closes on dismiss or after navigation kicks off.
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  // Driver AI assistant — the sheet is a single AppShell-level instance
  // driven by the evieChat store; this page just opens it via the FAB and
  // binds the charge-start context (surface copy, charger id, prefilled
  // support path) into the store while mounted.
  const evieOpen = useEvieChatStore((s) => s.open);
  const openEvie = useEvieChatStore((s) => s.openSheet);
  const authUser = useAuthStore((s) => s.user);
  // Phase 10 hook reused for the proactive predicate. mode +
  // canSwitch flow into the access check below.
  const { mode: currentMode } = useCurrentMode();
  // Selected payment source. `null` until preview loads; once derived
  // from preference + availability, the selector becomes interactive.
  const [selected, setSelected] = useState<'card' | 'wallet' | null>(null);
  // The driver tapped a wallet that cannot pay yet: the selector shows why,
  // with the top-up that fixes it, instead of navigating on the tap.
  const [walletExplained, setWalletExplained] = useState(false);

  // Live cards (28 Oct 2026): charger events refetch it, so with the
  // stream live it does not poll. The 10s fallback matches the heartbeat
  // window with tolerance and pauses on a hidden tab.
  const chargerRefreshMs = useStreamAwareInterval(10_000);
  const chargerQ = useQuery({
    queryKey: ['public-charger', chargerIdentity],
    queryFn: () => fetchPublicCharger(chargerIdentity),
    refetchInterval: chargerRefreshMs,
    refetchIntervalInBackground: false,
    staleTime: 5_000,
  });

  // Resolve the location FROM THE CHARGER, not from the URL path id. Field QR
  // stickers are frozen hardware: they encode the location by its internal
  // Mongo id, which the wipe-and-rebuild replaces with a new one, so fetching
  // by the URL `locationId` 404s after a rebuild and the page would hard-block
  // even though the charger resolved fine. The charger (resolved above by its
  // stable OCPP identity, which the mirror preserves) carries its CURRENT
  // location_id, so derive the location from that -- the same `enabled`-gated
  // pattern the tariff query below uses. The URL `locationId` stays the frozen
  // sticker value, used only for the URL bar + diagnostic copy, never for
  // resolution or in-app navigation -- the Back button derives its station
  // from the charger too, so it also survives a dead URL id after a rebuild.
  const resolvedLocationId = chargerQ.data?.location_id ?? null;
  const locationQ = useQuery({
    queryKey: ['public-location', resolvedLocationId],
    queryFn: () => fetchPublicLocation(resolvedLocationId!),
    enabled: Boolean(resolvedLocationId),
  });

  // A sticker whose charger no longer resolves (a v1 placeholder id retired
  // before the move, a unit swapped since) still names its site, so open that
  // site's charger list instead of a dead end (P0 2026-10-23). Replace, not
  // push, so Back skips the dead link; App's popstate listener re-routes.
  const stickerSiteQ = useQuery({
    queryKey: ['public-location', 'sticker', locationId],
    queryFn: () => fetchPublicLocation(locationId),
    enabled: chargerQ.isError,
    retry: false,
  });
  const stickerSiteId = chargerQ.isError ? (stickerSiteQ.data?._id ?? null) : null;
  useEffect(() => {
    if (!stickerSiteId) return;
    window.history.replaceState({}, '', `/stations/${stickerSiteId}`);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, [stickerSiteId]);

  // Tariff: prefer the charger's explicit tariff_id, else the location's
  // default. The `enabled` gate keeps the query idle until we know which
  // ID to fetch.
  const tariffId = chargerQ.data?.tariff_id ?? null;
  const tariffQ = useQuery({
    queryKey: ['public-tariff', tariffId],
    queryFn: () => fetchPublicTariff(tariffId!),
    enabled: Boolean(tariffId),
  });

  const paymentQ = useQuery({
    queryKey: ['payment-methods'],
    queryFn: fetchPaymentMethods,
  });

  // Pre-session start preview — runs the gate + computes the pre-auth
  // amount + reads lockout/preference state. Deliberately not refetched
  // on a tight interval; the data is "what would happen if Start were
  // tapped right now," not live telemetry. Refetched on mount, on
  // window focus (default), and explicitly after a wallet top-up
  // succeeds (via `previewQ.refetch()`).
  const previewQ = useQuery({
    queryKey: ['start-preview', chargerIdentity],
    queryFn: () => fetchStartPreview(chargerIdentity),
    enabled: Boolean(chargerIdentity),
    staleTime: 5_000,
  });

  // The wallet page's read (same key, per signed-in user): it carries the
  // driver's make-good credits (2026-10-28), which default the choice below
  // to the wallet.
  const cashWalletQ = useQuery({
    queryKey: cashWalletQueryKey(authUser?._id),
    queryFn: fetchCashWallet,
    staleTime: 15_000,
  });

  // Surface the "went offline mid-view" signal as a toast so the
  // operator's mental model isn't silently broken.
  useEffect(() => {
    if (chargerQ.data == null) return;
    const nowOnline = chargerQ.data.is_online;
    if (wasOnline === true && !nowOnline) {
      toast(t('toast.wentOffline'), { tone: 'warn' });
    }
    setWasOnline(nowOnline);
  }, [chargerQ.data, wasOnline]);

  const charger = chargerQ.data;
  const location = locationQ.data;

  // Bind the charge-start context into the global EVII sheet while this
  // page is mounted: charge-surface copy, the charger identity for the
  // agent's status tools, and the prefilled support path. Cleared on
  // unmount so EVII falls back to 'general' elsewhere.
  const setEvieContext = useEvieChatStore((s) => s.setContext);
  const clearEvieContext = useEvieChatStore((s) => s.clearContext);
  // Stand down inside the exit-overlay clone: AppShell mounts a live copy
  // of this page for the 280ms slide-down, and without the guard the
  // clone's bind/clear would clobber the context the NEXT page just set.
  const inExitOverlay = useContext(ExitOverlayContext);
  useEffect(() => {
    if (inExitOverlay) return;
    const params = new URLSearchParams({
      charger: charger?.identity ?? '',
      location: locationId,
    });
    setEvieContext({
      surface: 'charge-start',
      chargerId: charger?.identity ?? null,
      supportPath: `/support?${params.toString()}`,
    });
    return () => clearEvieContext();
  }, [charger?.identity, locationId, setEvieContext, clearEvieContext, inExitOverlay]);
  const tariff = tariffQ.data;
  const paymentMethods = paymentQ.data;
  const preview = previewQ.data;
  // "Does this driver have a card on file" — two signals, preferring
  // the live payment-methods query when present (it catches an
  // add/remove inside this same tab session) and falling back to the
  // auth store's server-derived flag on first render.
  //
  // THIS IS A LABEL, NOT A GATE. It answers "has a card", which is a
  // strictly narrower question than "can start a session here", and
  // until 2026-09-05 it was wired to the Start button as though the two
  // were the same. They are not: a driver with a funded wallet and no
  // card can charge at their own tenant's chargers, and the gate says
  // so. The card row below is the only thing that should read this.
  const hasCardOnFile = paymentMethods
    ? Boolean(paymentMethods.default_payment_method)
    : authUser?.has_payment_method ?? false;

  // The card a card-paid start would hold against has expired. Known
  // before Start: the preview names the start card and the payment-methods
  // list carries its expiry (Warren, 21 October). The driver is told up
  // front and a card-paid start is never sent, so it cannot fail at the
  // server or the charger. Unknown counts as not expired; the server's
  // PRE_AUTH_FAILED banner stays the backstop.
  const cardExpiredBeforeStart = Boolean(preview?.card.available)
    && startCardExpired(preview?.card.paymentMethodId, paymentMethods);

  // The wallet option: the start gate's own verdict, so the selector offers
  // the wallet exactly when the start would take it (2026-10-25). A CAD
  // charger is no longer card-only: a CAD wallet pays it.
  const walletView = preview ? walletOptionView(preview) : null;
  // A make-good credit this start would use (Warren, 2026-10-28): in the
  // currency of the wallet that would pay, while the wallet can pay here.
  const credit = startCredit(walletView, cashWalletQ.data?.credits);

  // Derive the initial selection once preview lands. After that the
  // user owns `selected` via the segmented control. The default-pick
  // logic mirrors the gate's own preference handling so the UI shows
  // the same source the gate would resolve to on tap, except that an
  // unspent make-good credit opens on Wallet (Start then sends that
  // choice for that start only: startPaymentSource). It waits for the
  // wallet read, and for a refetch of a cached one, so a credit is never
  // missed, nor a spent one used, by a selection made before it arrived.
  useEffect(() => {
    if (!preview) return;
    if (selected !== null) return;
    if (cashWalletQ.isPending || cashWalletQ.isFetching) return;
    setSelected(pickInitialSource(preview, cardExpiredBeforeStart, credit !== null));
  }, [preview, selected, cardExpiredBeforeStart, cashWalletQ.isPending, cashWalletQ.isFetching, credit]);

  // Gate-blocked states (PAYMENT_LOCKED / INSUFFICIENT_WALLET when no
  // card path) suppress the selector entirely and disable the CTA.
  // NO_PAYMENT_METHOD already routes to the existing add-payment
  // CTA branch below.
  //
  // OUTSIDE_GUEST_WINDOW (commit 12 of third-party-charger-access)
  // is a separate guest-only block: the gate would have ok=true on
  // its wallet/card axis, but the guest verdict says the windows
  // are closed right now. Treat as a block-with-banner; the banner
  // copy uses guestAccess.nextOpenAt for the relative-time hint.
  const guestWindowBlocked =
    Boolean(preview?.guestAccess)
    && preview!.guestAccess!.reason === 'OUTSIDE_GUEST_WINDOW';
  // GUEST_ACCESS_DISABLED is the other guest refusal and it was not
  // listed here. The wallet/card gate can return ok=true for a guest at
  // a charger that is simply closed to guests — the two verdicts are
  // computed independently and the guest one is evaluated FIRST at
  // start (a 403 before the wallet gate is ever consulted). Leaving it
  // out meant the CTA stayed enabled and the driver only found out on
  // tap. Unlike the window case there is no "available again at" to
  // offer: the charger is not open to them at all.
  const guestAccessDisabled =
    Boolean(preview?.guestAccess)
    && preview!.guestAccess!.reason === 'GUEST_ACCESS_DISABLED';
  // Item 6: the same refusal at a resident's charger that is open to
  // guests with no guest price. Still a block (same reason); only the
  // words change: "This charger isn't open to guests yet".
  const guestPriceNotSet = guestAccessDisabled && isPriceNotSet(preview?.guestAccess);
  const gateBlocked =
    Boolean(preview)
    && (
      (
        !preview!.ok
        && (preview!.reason === 'PAYMENT_LOCKED'
            || preview!.reason === 'OUTSTANDING_BALANCE'
            || preview!.reason === 'INSUFFICIENT_WALLET'
            // Fleet-sponsored start with no card path. Listed here so
            // the block surfaces at all — an unlisted reason leaves the
            // CTA enabled and the driver only learns on tap. Its CTA is
            // "add a card", NOT top-up: the fleet pays, so a wallet
            // balance can never unblock this.
            || preview!.reason === 'FLEET_SPONSORED_NO_CARD_PATH')
      )
      || guestWindowBlocked
      || guestAccessDisabled
    );

  // Phase 3D — proactive Fleet-Mode access predicate. Runs the same
  // resolveChargerAccessibility helper the map markers use (Phase 9),
  // BEFORE the driver taps Start. When the verdict is anything other
  // than 'accessible', synthesize a DriverStartError-shaped object
  // so the existing Phase 10 AccessDeniedBanner + StickyCTA-disabled
  // paths kick in without needing a server round-trip first.
  //
  // The server gate at /chargers/:id/driver-start is still
  // authoritative — this is a client-side prediction that mirrors
  // the gate's decision table. If the prediction is wrong (e.g.,
  // stale local data), the actual server response on tap rolls
  // into the same banner via the existing startError path.
  //
  // Mode switches re-evaluate via useCurrentMode — when the driver
  // taps "Switch to fleet mode" in the pre-rendered banner, the
  // hook flips current_mode, this useMemo re-runs, the verdict
  // becomes 'accessible', proactiveAccessError clears, the CTA
  // re-enables. No retry of handleStart needed for the proactive
  // path; the original tap is now valid.
  const proactiveAccessError = useMemo<DriverStartError | null>(() => {
    if (!charger) return null;
    if (charger.commissioning_test_allowed) return null;
    // Only run when we have the server-derived fleet metadata.
    // Old API instances that haven't deployed the projection
    // extension leave fleet_access_mode undefined; the helper
    // defaults to 'public' which would mis-classify fleet chargers.
    // Skip the predicate entirely on missing data — the server
    // gate is still the safety net.
    if (charger.fleet_access_mode === undefined) return null;

    const verdict = resolveChargerAccessibility(
      {
        fleet_id: charger.fleet_id ?? null,
        fleet_access_mode: charger.fleet_access_mode,
      },
      {
        fleet_id: authUser?.fleet_id ?? null,
        current_mode: currentMode,
      },
    );
    if (verdict === 'accessible') return null;

    // Map verdict + state to a SessionAccessReason. Mirrors the
    // server-side resolveSessionAccess decision table (Phase 2).
    const sameFleet = Boolean(
      authUser?.fleet_id
      && charger.fleet_id
      && String(authUser.fleet_id) === String(charger.fleet_id),
    );
    let reason: SessionAccessReason;
    let hint: string;
    if (verdict === 'mode_mismatch') {
      const target = modeMismatchTarget(
        {
          fleet_id: charger.fleet_id ?? null,
          fleet_access_mode: charger.fleet_access_mode,
        },
        {
          fleet_id: authUser?.fleet_id ?? null,
          current_mode: currentMode,
        },
      );
      if (target === 'public') {
        reason = 'MUST_SWITCH_TO_PUBLIC';
        hint = t('hint.mustSwitchPublic');
      } else {
        reason = 'MUST_SWITCH_TO_FLEET';
        hint = t('hint.mustSwitchFleet');
      }
    } else {
      // inaccessible — distinguish DIFFERENT_FLEET from
      // NOT_IN_THIS_FLEET so the banner copy reads cleanly.
      if (sameFleet) {
        // Shouldn't happen — same-fleet users in fleet mode at a
        // fleet_only charger pass the gate. Defensive fallback.
        reason = 'NOT_IN_THIS_FLEET';
        hint = t('hint.notInThisFleet');
      } else if (authUser?.fleet_id) {
        reason = 'DIFFERENT_FLEET';
        hint = t('hint.differentFleet');
      } else {
        reason = 'NOT_IN_THIS_FLEET';
        hint = t('hint.notInThisFleet');
      }
    }
    return {
      error: 'SESSION_ACCESS_DENIED',
      code: reason,
      hint,
    } satisfies DriverStartError;
  }, [charger, authUser?.fleet_id, currentMode, t]);

  // Effective startError = explicit server error (post-tap rejection)
  // OR the proactive prediction. Server error always wins so a real
  // rejection's exact code surfaces over the prediction.
  // An expired start card is a proactive block of its own: the same
  // "Your card has expired" banner the server's refusal gets, shown before
  // Start. It blocks a start that would hold on the card; a wallet-paid
  // start still goes, with the banner shown.
  const proactiveCardError: DriverStartError | null = cardExpiredBeforeStart
    ? { error: 'CARD_EXPIRED', code: 'CARD_EXPIRED' }
    : null;
  const effectiveStartError = startError ?? proactiveAccessError ?? proactiveCardError;
  const accessBlockedProactively = startError === null && proactiveAccessError !== null;
  const cardBlockedProactively = startError === null && !accessBlockedProactively
    && expiredCardBlocksStart(cardExpiredBeforeStart, selected, walletView !== null && !walletView.selectable);

  // Guest-pricing markup as a percentage string for copy
  // ("+8.5%" etc.). Empty when this isn't a guest session OR the
  // markup isn't set. The badge / copy callers test on truthiness.
  const guestMarkupPct = (() => {
    if (preview?.accessMode !== 'guest') return '';
    const bps = preview.guestMarkupBps;
    if (typeof bps !== 'number' || bps <= 0) return '';
    // 1 dp keeps "+20%" clean and "+8.5%" precise without noise.
    const pct = bps / 100;
    return Number.isInteger(pct) ? `${pct}%` : `${pct.toFixed(1)}%`;
  })();

  const isLoading = chargerQ.isLoading || locationQ.isLoading
    || (chargerQ.isError && (stickerSiteQ.isLoading || Boolean(stickerSiteId)));
  // The plug the driver picked on the station page for a multi-plug
  // charger, carried as ?plug=<connector_id>. Read once per stall: the
  // route is a pushState from StationDetailPage or a QR / deep link, and
  // only this page's own switcher rewrites the query while mounted (via
  // `pick` below, which shadows this). Absent on single-plug rows, older
  // deep links and the stall QR stickers.
  const requestedPlug = useMemo(() => readPlugFromUrl(), [locationId, chargerIdentity]);
  // The driver's in-page pick from the plug switcher. Tagged with the
  // charger identity rather than reset in an effect: App's switch-based
  // router can re-render this component for a different stall without
  // remounting it, and a pick made on one charger must not leak onto the
  // next. `plug: null` is the explicit "ignore the URL" state set after
  // the server rejects the URL's plug (INVALID_CONNECTOR), so the
  // status-aware default takes over instead of re-sending a bad id.
  const [pick, setPick] = useState<{ identity: string; plug: number | null } | null>(null);
  const wantedPlug = pick !== null && pick.identity === chargerIdentity
    ? pick.plug
    : requestedPlug;
  const connectors = charger?.connectors ?? [];
  const activeConnector = resolveActiveConnector(
    connectors,
    wantedPlug,
    charger?.is_online ?? false,
  );
  // Two or more physical plugs on one OCPP charger (the dual-port Autel
  // AC Ultra). Drives the plug switcher, the extra "Plug" spec card, the
  // per-plug hero pill and the CTA suffix so the driver can confirm
  // which port they are about to start.
  const isMultiPlug = connectors.length > 1;
  const activePlug = activeConnector?.connector_id ?? 1;
  // The picked plug's own status view — only read on multi-plug stalls,
  // where the hero pill shows THIS plug rather than the charger.
  const activeView = viewForConnectorStatus(activeConnector?.status, charger?.is_online ?? false);
  // Every plug busy (both Charging, one Finishing, a fault): the default
  // rule fell back to plug 1 with nothing selectable. The hero pill and
  // the chips already say so; the CTA must agree, or it invites a tap
  // that mints and cancels a pre-auth, leaves a failed Session row and
  // ends in a generic error. The 10s charger poll re-enables it the
  // moment a plug frees. Single-plug stalls keep the server-decides
  // policy this page has always had.
  const noPlugSelectable = isMultiPlug && !activeView.selectable;
  const waitingForPlug = !!charger?.preparing_required_connectors?.includes(activePlug)
    && activeConnector?.status !== 'Preparing'
    // The one other plug is Preparing: the Api starts that one (the
    // wrong-connector redirect), so the tap is not held here.
    && !apiMayStartOtherPlug(connectors, activePlug);
  // Switcher row, so a plug rejection can bring it into view.
  const plugRowRef = useRef<HTMLDivElement | null>(null);
  const handlePickPlug = (connectorId: number) => {
    setPick({ identity: chargerIdentity, plug: connectorId });
    writePlugToUrl(connectorId);
    // A plug rejection is about the plug just left, so it clears on
    // switch. Every other post-tap error (wallet gate, offline,
    // timeout) is about the driver or the charger and stays put — its
    // banner may carry a remediation CTA the driver still needs.
    setStartError((e) => (e?.code === 'INVALID_CONNECTOR' ? null : e));
  };
  const inMaint = useMemo(() => {
    if (!charger?.maintenance_windows) return false;
    const now = new Date();
    return charger.maintenance_windows.some(
      (w) => new Date(w.start) <= now && now <= new Date(w.end),
    );
  }, [charger]);

  // Derive the CTA state once so the render stays readable.
  //
  // Driver-friction fix (Layer 2): we no longer swap the CTA to
  // 'add-payment' when no card is on file. Instead the button keeps
  // reading "Start charging" and `handleStart` intercepts the tap
  // with the educational modal before any server call. This makes
  // the no-card path obvious — drivers were silently routed to the
  // payment-methods page before, which felt like an arbitrary
  // redirect without explanation.
  const ctaState: 'idle' | 'offline' | 'maint' | 'disabled-inactive' | 'loading' | 'error' =
    starting
      ? 'loading'
      : startError
        ? 'error'
        : !charger?.is_active
          ? 'disabled-inactive'
          : inMaint
            ? 'maint'
            : !charger?.is_online
              ? 'offline'
              : 'idle';

  const handleStart = async () => {
    if (!charger) return;
    // ── Block-and-explain, driven by the GATE'S verdict ─────────────
    //
    // This used to read `!hasPaymentMethod` — "no card on file" — and
    // open the add-payment modal. That blocked, before any server call,
    // every driver who can pay some other way. A wallet-funded driver
    // with no card is allowed by canUserStartSession; the client simply
    // never asked. The v1 mirror makes that population large: it copies
    // stripe_customer_id and never writes
    // default_stripe_payment_method_id, and it carries wallet balances.
    //
    // `preview` is GET /chargers/:id/start-preview, already fetched
    // above. It runs the real gate for THIS driver at THIS charger and
    // returns its ok/reason verbatim, so it knows everything a
    // user-level flag structurally cannot: the operator's minimum
    // balance, the cross-tenant wallet guard, fleet sponsorship,
    // lockout, tier credit, and whether the session bills at all.
    //
    // Keep the modal for the case it was written for — a driver whose
    // only problem IS the missing card — and let every other allowed
    // driver through.
    //
    // The gate pays with the stored choice, not the screen. When they
    // differ (Wallet opened by a make-good credit over a stored Card),
    // the checked one goes with this start (payment_source, below), for
    // this start only: nothing is saved, so the driver's RFID card keeps
    // paying the way they left it. A usable wallet sent that way needs no
    // card.
    const oneStartSource = startPaymentSource(selected, preview?.preferredPaymentSource ?? null, Boolean(preview?.card.available));
    if (preview) {
      const needsCard =
        !preview.ok
        && (preview.reason === 'NO_PAYMENT_METHOD'
            || preview.reason === 'NO_STRIPE_CUSTOMER')
        && !(oneStartSource === 'wallet' && walletView?.selectable);
      if (needsCard) {
        setShowAddPaymentModal(true);
        return;
      }
      // Every other !ok reason is either already surfaced by
      // `gateBlocked` (which disables the CTA, so we are not reachable)
      // or is something the server should answer authoritatively. Fall
      // through to the call and let the 402 render in the error banner.
    }
    // preview === undefined — the query has not resolved, or it failed.
    //
    // EXPLICIT POLICY: unknown is NOT blocked. We let the tap through to
    // the server, which runs the authoritative gate and returns a typed
    // 402 that `startError` renders with its own retry. Blocking on an
    // absent preview would reproduce the exact bug this change removes,
    // just with a different trigger — and it would do it silently, since
    // a failed query looks identical to a driver who cannot pay.
    //
    // The cost of being wrong in this direction is one server round trip
    // and a banner. The cost of being wrong in the other direction is a
    // driver standing at a charger they are entitled to use.
    if (recoveryId && recoveryConfirmedKey !== `${chargerIdentity}:${activePlug}`) {
      toast(recoveryText(i18n.language, 4), { tone: 'warn' });
      return;
    }
    setStartError(null);
    setStarting(true);
    try {
      const started = await startDriverCharger(charger.identity, {
        connector_id: activePlug,
        ...(recoveryId ? { recovery_session_id: recoveryId, recovery_confirmed: true } : {}),
        // The checked choice when the stored one would pay otherwise (see
        // oneStartSource above): this start only, never saved.
        ...(oneStartSource ? { payment_source: oneStartSource } : {}),
      });
      // The pending shell exists server-side now; refetch the shared
      // live-session cache so the home (HomeRoute) and the mini bar see
      // it at once rather than one 15s poll later.
      qc.invalidateQueries({ queryKey: ACTIVE_SESSION_KEY });
      // The Api started the other plug, where the car is plugged in
      // (Warren, 29 Oct 2026): say which, long enough to read at the unit.
      const plugNotice = startedPlugNotice(started);
      // Or another charger at the site, where the car is plugged in (wrong
      // pedestal, Warren 29 Oct 2026): say which, by the name on the unit.
      const chargerNotice = startedChargerNotice(started);
      // The charger still held an earlier start: the Api resends this one by
      // itself in a moment, so the driver waits on the same screen.
      const queuedSeconds = startQueuedSeconds(started);
      if (queuedSeconds !== null) {
        toast(t('toast.startQueued', { seconds: queuedSeconds }), { tone: 'accent', durationMs: 8000 });
      } else if (chargerNotice) {
        toast(t('toast.startedOtherCharger', { name: chargerNotice.name }), { tone: 'accent', durationMs: 8000 });
      } else if (plugNotice) {
        toast(t('toast.startedOtherPlug', { plug: plugNotice.plug }), { tone: 'accent', durationMs: 6000 });
      } else {
        toast(t('toast.sessionStarted'), { tone: 'accent' });
      }
      // Transition to the active-session UI. The session's
      // transactionId isn't part of the RemoteStartTransaction
      // response (the charger fires StartTransaction.req back on
      // its own after the driver plugs in), so route to a sentinel
      // path. ChargingPage's `sessionId !== 'demo'` branch leaves
      // isDemo=false, so the activeQ poll (4s cadence) kicks in
      // and resolves the user's actual session within seconds of
      // OCPP StartTransaction. While liveSession is still null
      // ChargingPage renders the "Waiting for charger…" state.
      //
      // The earlier '/charging/demo' redirect bypassed all this —
      // demo flips isDemo=true, disables the poll, and renders
      // synthetic metrics. Real users tapping a real Start button
      // saw fake numbers (battery climbing 12%→99.9% over 600s)
      // until they manually navigated away.
      //
      // C0 charging-flow correctness: pass the charger identity as
      // a URL param so ChargingPage's Cancel handler has it for the
      // by-charger fallback (cancelDriverChargerSession). Without
      // this the "Waiting for charger" Cancel can't reach the
      // server-side session-lookup fallback when the active-session
      // query hasn't yet primed a Session._id.
      onNavigate(`/charging/active?charger=${encodeURIComponent(chargerNotice?.identity ?? charger.identity)}`);
    } catch (err) {
      const asDriverErr = err as DriverStartError;
      setStartError(asDriverErr);
      // The private switch turned on after this screen loaded: re-read, and
      // the page turns into the "This is a private charger" notice.
      if (isPrivateChargerError(asDriverErr)) {
        void chargerQ.refetch();
        void previewQ.refetch();
      }
      if (asDriverErr.code === 'INVALID_CONNECTOR') {
        // The server's connectors[] disagrees with ours about the plug
        // we sent — a stale ?plug= from before a rebuild, or the list
        // changed between the last poll and the tap. Stop trusting the
        // URL, drop the bad id from it, refetch so the switcher shows
        // the plugs that ARE on this charger, and bring the switcher
        // into view so the driver picks from them. The retry on the
        // red CTA then sends whatever the status-aware default resolves.
        setPick({ identity: chargerIdentity, plug: null });
        writePlugToUrl(null);
        void chargerQ.refetch();
        const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        plugRowRef.current?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
      }
    } finally {
      setStarting(false);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <Loader2 size={20} className={styles.spin} />
          <p>{t('loading')}</p>
        </div>
      </div>
    );
  }

  // The private switch (Warren, 2026-10-22): a private charger this driver
  // may not start (not on its pass list, not its host, not EVI staff). The
  // Api says so on the charger read or the preview; the page says "This is
  // a private charger" in place of the Start button, and nothing else: no
  // payment selector, no plug picker, nothing to tap. Checked before the
  // station read, which may list none of this site's stalls for them.
  if (charger && isPrivateLocked(charger, preview)) {
    return (
      <div className={styles.page}>
        <header className={styles.backRow}>
          <button type="button" className={styles.backBtn} onClick={() => onNavigate('/map')}>
            <ArrowLeft size={16} /> {t('backToMap')}
          </button>
        </header>
        <PrivateChargerNotice
          ns="preSession"
          className={styles.errorCard}
          titleClassName={styles.errorTitle}
          hintClassName={styles.errorHint}
        />
      </div>
    );
  }

  // Two independent failure modes to tell apart. Either one blocks
  // the page (we can't render Start without a charger), but the
  // driver-facing copy should say which resource went missing so
  // they know whether to recheck the QR or report a bad deep link.
  //
  //   chargerQ fails      → charger identity / ObjectId didn't resolve
  //   locationQ fails     → location ID didn't resolve; charger may
  //                         still be valid, but the breadcrumb hero
  //                         can't render so we block here too
  //   both fail           → most likely a stale/wrong URL entirely
  const chargerMissing = chargerQ.isError || !charger;
  const locationMissing = locationQ.isError || (!locationQ.isLoading && !location);
  if (chargerMissing || locationMissing) {
    return (
      <div className={styles.page}>
        <header className={styles.backRow}>
          <button type="button" className={styles.backBtn} onClick={() => onNavigate('/map')}>
            <ArrowLeft size={16} /> {t('backToMap')}
          </button>
        </header>
        <div className={styles.errorCard}>
          <AlertTriangle size={24} />
          <p className={styles.errorTitle}>
            {chargerMissing && locationMissing
              ? t('error.cantOpen')
              : chargerMissing
                ? t('error.chargerNotFound')
                : t('error.stationNotFound')}
          </p>
          {/* Mode-neutral copy — pre-Phase-3.5 these read "in your
              account" / "isn't visible to your account" which is
              fleet-shaped framing that breaks for public-mode
              drivers scanning a foreign-tenant or unreachable
              stall. The 404 here means the charger isn't visible
              to the user via any of (own tenant, public_network +
              is_open_to_guests, guest_pass_list grant) — better
              described as "isn't available" + the
              public-charging hint than as "doesn't exist in your
              account." */}
          <p className={styles.errorHint}>
            {chargerMissing && locationMissing
              ? t('error.bothHint', { locationId, chargerIdentity })
              : chargerMissing
                ? t('error.chargerHint', { chargerIdentity })
                : t('error.stationHint', { locationId })}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <PhotoHero
        src={location?.photo_url?.trim() || HERO_PLACEHOLDER}
        alt=""
        height={160}
        topLeft={
          // A start begun on the map's sheet (?from=map) goes back to the
          // map; a QR sticker or a deep link keeps the station page.
          <button
            type="button"
            className={styles.backBtn}
            onClick={() => onNavigate(preSessionBackPath(window.location.search, charger.location_id))}
          >
            <ArrowLeft size={14} /> {t('back')}
          </button>
        }
        topRight={
          inMaint ? (
            <span className={`${styles.heroPill} ${styles.pillWarn}`}>{t('status.maintenance')}</span>
          ) : !charger.is_online ? (
            <span className={`${styles.heroPill} ${styles.pillOffline}`}>
              <WifiOff size={11} /> {t('status.offline')}
            </span>
          ) : isMultiPlug ? (
            // The PICKED plug's status, not the charger's: on a dual-
            // port unit "Available" was true of the charger while the
            // plug the CTA would start was Charging. Single-plug stalls
            // keep the charger-level pill below, unchanged.
            <span className={`${styles.heroPill} ${HERO_PILL_TONE_CLASS[activeView.tone]}`}>
              {connectorStatusLabel(t, activeView)}
            </span>
          ) : (
            <span className={`${styles.heroPill} ${styles.pillOk}`}>{t('status.available')}</span>
          )
        }
        bottomLeft={
          <div className={styles.heroFoot}>
            <h1 className={styles.heroTitle}>{location?.name ?? t('heroFallback')}</h1>
            {location && (
              <p className={styles.heroAddr}>
                <MapPin size={11} /> {location.address.street}, {location.address.city}
              </p>
            )}
          </div>
        }
      />

      {/* Offline banner — only rendered above the fold when relevant
          so the happy-path screen stays quiet. */}
      {!charger.is_online && !inMaint && (
        <div className={styles.banner}>
          <WifiOff size={14} />
          <div>
            <p className={styles.bannerTitle}>{t('offlineBanner.title')}</p>
            <p className={styles.bannerHint}>
              {t('offlineBanner.body')}
            </p>
          </div>
        </div>
      )}

      {/* Plug switcher — multi-plug chargers only. One chip per
          physical plug with its own live status, mirroring the station
          page's MultiPlugRow so a driver who arrived by QR (no plug in
          the URL) sees the same picker they would have had on the
          station page. Chips are tappable only when THAT plug is
          selectable; the selected chip is whatever
          resolveActiveConnector settled on, which on a scan is the
          first free plug rather than plug 1. A disabled-but-selected
          chip (nothing selectable) is deliberate: it names the plug
          the gates below are talking about. */}
      {isMultiPlug && (
        <div
          ref={plugRowRef}
          className={styles.plugRow}
          role="group"
          aria-label={t('plug.listAria', { identity: charger.identity })}
        >
          {connectors.map((conn) => {
            const view = viewForConnectorStatus(conn.status, charger.is_online);
            const isSelected = conn.connector_id === activePlug;
            return (
              <button
                key={conn.connector_id}
                type="button"
                onClick={view.selectable ? () => handlePickPlug(conn.connector_id) : undefined}
                disabled={!view.selectable}
                aria-pressed={isSelected}
                className={`${styles.plugChip} ${isSelected ? styles.plugChipSelected : ''}`}
              >
                <RadioDot selected={isSelected} disabled={!view.selectable} />
                <span className={styles.plugChipLabel}>
                  <span className={styles.plugChipName}>
                    {t('plug.label', { n: conn.connector_id })}
                  </span>
                  <span className={styles.plugChipSpec}>
                    {[conn.type, t('plug.kw', { kw: conn.max_power_kw })].filter(Boolean).join(' · ')}
                  </span>
                </span>
                <StatusPill tone={view.tone} variant="soft" size="sm">
                  {connectorStatusLabel(t, view)}
                </StatusPill>
              </button>
            );
          })}
        </div>
      )}

      {/* Spec row — charger identity, connector type, max kW. A multi-
          plug charger adds the picked plug between them (2x2 grid) so
          the driver sees which port the CTA below will start; the type
          and kW cards already describe THAT plug, not connectors[0]. */}
      <section className={`${styles.specGrid} ${isMultiPlug ? styles.specGridPlug : ''}`}>
        <StatCard label={t('spec.stall')} value={charger.identity} />
        {isMultiPlug && (
          <StatCard label={t('spec.plug')} value={activePlug} />
        )}
        <StatCard
          label={t('spec.connector')}
          value={activeConnector?.type ?? '—'}
        />
        <StatCard
          label={t('spec.maxPower')}
          value={activeConnector?.max_power_kw ?? '—'}
          unit="kW"
          tone="power"
        />
      </section>

      {/* Tariff row — one-liner, tap to expand. Prefers the tariff
          the START will actually bill (start-preview's resolved
          tariff: Home Guest for paying guests at homes; also the
          only rate a cross-tenant guest can read at all, since the
          direct /tariffs/:id fetch is tenant-scoped and 404s for
          them). Discrimination matters: an EXPLICIT tariff:null from
          the preview means "nothing resolved — the session bills the
          flat fallback", and must NOT fall back to the tenant fetch
          (which happily returns retired/inactive docs that won't
          bill); only a response predating the field falls back.
          Loading holds until a rate exists or BOTH sources settled,
          so the noRate copy never flashes while the other query is
          still in flight. */}
      {(() => {
        const previewData = previewQ.data;
        const displayTariff = previewData && 'tariff' in previewData
          ? previewData.tariff ?? undefined
          : tariff;
        // EVI's connection fee for this start (Tier 2), from the preview:
        // kept by EVI, never the host's, 0 when the session cannot bill.
        // A Tier 2 preview also rewrites rates.flat_fee to this number,
        // so once the field is present rates.flat_fee is EVI's fee, not
        // an operator's, and is never shown a second time as "Session
        // fee". Only a response that predates the field (or the
        // tenant-fetch fallback) still quotes the tariff's own fee.
        const connectionFeeCents = previewData?.connectionFeeCents;
        // A per-hour tariff bills the whole stay and never bills idle, so
        // its time_rate is not an idle fee to quote.
        const idleFeeCents = displayTariff?.rate_type === 'per_hour'
          ? 0
          : Math.min(IDLE_RATE_MAX_CENTS_PER_MIN, Math.max(0, displayTariff?.rates.time_rate ?? 0));
        // Its grace (Warren, 2026-09-28): shown with the fee, and the
        // default for a tariff saved before the field, which is what
        // billing pins for it.
        const idleGraceMinutes = resolveIdleGraceMinutes(undefined, displayTariff?.rates.idle_grace_minutes);
        return (
          <>
            <TariffRow
              tariff={displayTariff}
              loading={!displayTariff && (tariffQ.isLoading || previewQ.isLoading)}
              connectionFeeCents={connectionFeeCents}
            />

            {/* Session limits.
                Reads displayTariff, NOT the raw tenant-scoped `tariff`
                fetch — the same discrimination TariffRow above already
                makes. The direct /tariffs/:id read is tenant-scoped and
                404s for a cross-tenant guest, which is precisely the
                driver this card exists to inform: a guest at someone
                else's home saw an em-dash for both fees no matter what
                the host had set, while the start-preview's resolved
                tariff (Home Guest for a paying guest) carried the real
                numbers all along.

                Max duration has no field anywhere in the schema and
                nothing enforces one, so "No cap" is literally true
                rather than a placeholder. Per the ruling, auto-stop
                stays unbuilt until push notifications exist — a forced
                stop the driver cannot be told about is worse than no
                cap at all.

                The third cell is EVI's connection fee, labelled as EVI's.
                The tariff's own session fee is retired and never bills a
                new session, so it is never quoted here — not even when the
                preview has not resolved and displayTariff fell back to the
                raw tariff read, which still carries a legacy flat_fee.
                Web deploys after Api and Ocpp, so this page never meets an
                Api that bills the tariff fee. */}
            <section className={styles.limitsCard}>
              <div className={styles.limitsHeader}>
                <Clock size={14} />
                <span>{t('limits.title')}</span>
              </div>
              <div className={styles.limitsBody}>
                <div>
                  <p className={styles.limitLabel}>{t('limits.maxDuration')}</p>
                  <p className={styles.limitValue}>{t('limits.noCap')}</p>
                </div>
                <div>
                  <p className={styles.limitLabel}>{t('limits.idleFee')}</p>
                  <p className={styles.limitValue}>
                    {idleFeeCents
                      ? `${formatCents(idleFeeCents, displayTariff?.currency, i18n.language)}/min`
                      : '—'}
                  </p>
                  {/* Idle bills after the grace, for at most
                      IDLE_FEE_MAX_MINUTES a session (Warren, 2026-09-27/28):
                      "after charging, 15 minutes free, then 25 cents a
                      minute, up to 60 minutes", before they plug in. */}
                  {!!idleFeeCents && (
                    <p className={styles.limitNote}>
                      {renderInline(idleDisclosure({ ...displayTariff?.rates, time_rate: idleFeeCents,
                        idle_grace_minutes: idleGraceMinutes }, displayTariff?.currency, i18n.language, t))}
                    </p>
                  )}
                </div>
                <div>
                  <p className={styles.limitLabel}>{t('limits.connectionFee')}</p>
                  <p className={styles.limitValue}>
                    {(connectionFeeCents ?? 0) > 0
                      ? formatCents(connectionFeeCents ?? 0, displayTariff?.currency, i18n.language)
                      : '—'}
                  </p>
                </div>
              </div>
            </section>
          </>
        );
      })()}

      {/* Payment method row: only when the start preview could not load,
          so the payment selector (whose Card option names the card and
          carries "Change") is not shown. It used to sit above the selector
          too, saying the same thing twice (Warren, 2026-10-25). Clicking
          jumps to /billing. When no card is on file the "Start charging"
          CTA below intercepts the tap with an educational modal
          (driver-friction fix Layer 2) rather than silently rewriting
          itself. */}
      {!preview && previewQ.isError && (
      <button
        type="button"
        className={styles.paymentCard}
        onClick={() => onNavigate('/billing/payment-methods')}
      >
        <div className={styles.paymentIcon}>
          <NamedIcon name="creditcard" fallback={<CreditCard size={16} />} size={16} />
        </div>
        <div className={styles.paymentBody}>
          <p className={styles.paymentLabel}>{t('payment.label')}</p>
          {hasCardOnFile ? (
            <p className={styles.paymentValue}>
              {formatPaymentMethod(paymentMethods)}
            </p>
          ) : (
            <p className={styles.paymentValueWarn}>{t('payment.noCard')}</p>
          )}
        </div>
        <ChevronRight size={16} className={styles.paymentChev} />
      </button>
      )}

      {/* Fleet billing indicator (Phase 1 fleet platform). Renders
          only for drivers attributed to a Fleet — the spec is
          explicit about disclosure so the driver knows the session
          will charge the fleet's card not theirs. Inline-styled
          (one-off; the broader Fleet-UX pass can lift to a class). */}
      {authUser?.fleet_id && authUser.fleet_name && (
        <div
          style={{
            margin: '8px 0',
            padding: '10px 14px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: 'color-mix(in srgb, var(--evi-accent, #2ECC71) 12%, transparent)',
            border: '1px solid color-mix(in srgb, var(--evi-accent, #2ECC71) 35%, transparent)',
            borderRadius: 999,
            fontSize: 12,
            color: 'var(--color-tx)',
          }}
        >
          <CreditCard size={13} style={{ color: 'var(--evi-accent, #2ECC71)' }} />
          <span>
            {t('fleet.billedTo')} <strong>{authUser.fleet_name}</strong>
          </span>
        </div>
      )}

      {/* Payment selector + pre-auth copy.
          The selector renders whenever the preview has loaded: the wallet
          is always shown before a start, with its balance and either "Use
          wallet" or the exact reason it can't be used yet (Warren,
          2026-10-25). When the gate blocks (PAYMENT_LOCKED, INSUFFICIENT_
          WALLET without a card fallback, ...) the dedicated banner below
          says so and the CTA stays disabled; the pay copy shows only when
          a start can go. */}
      {walletView?.reason === 'CURRENCY_MISMATCH' && walletView.currency === 'USD'
        && walletView.sessionCurrency === 'CAD' && preview?.card.available && (
        // Still true here: the driver's money is USD and the price CAD.
        <p className={styles.payCopy}>{t('selector.cadCardOnly')}</p>
      )}
      {preview && (
        <>
          <PaymentSelector
            preview={preview}
            cardLast4={defaultCardSummary(paymentMethods).last4}
            cardBrand={defaultCardSummary(paymentMethods).brand}
            selected={selected}
            walletExplained={walletExplained}
            onExplainWallet={() => setWalletExplained(true)}
            onSelect={(source) => {
              // Card and Wallet toggle freely; selecting never navigates.
              setWalletExplained(false);
              setSelected(source);
              // UI persists locally; the server write syncs the User doc
              // the gate reads at start, then the preview re-runs the gate
              // so a block that the other source clears (wallet short,
              // card on file) lifts without a reload.
              void setPaymentSource(source).then(() => previewQ.refetch()).catch(() => {
                // Stale preference is harmless: the gate re-runs at
                // start-time and reads the canonical User field. If
                // the write failed, next page load reconciles.
              });
            }}
            onNavigate={onNavigate}
          />
          {!gateBlocked && selected === 'card' && preview.card.available && (
            <p className={styles.payCopy}>
              {/* The hold is placed in the price's currency (item 3): CA$50 at
                  a Canadian home. */}
              {preview.preAuthAmountCents === 0 ? t('payCopy.cardAtStop') : t('payCopy.cardHold', { amount: formatCents(preview.preAuthAmountCents, preview.tariff?.currency, i18n.language) })}
              {/* "Only charged for what you use" stops being true once
                  EVI's fixed connection fee applies; the limits card
                  above names that fee, so the line is simply dropped. */}
              {guestMarkupPct
                ? t('payCopy.guestMarkupSuffix', { pct: guestMarkupPct })
                : (preview.connectionFeeCents ?? 0) > 0
                  ? null
                  : t('payCopy.cardOnlyUsage')}
            </p>
          )}
          {!gateBlocked && selected === 'wallet' && walletView?.selectable && (
            <p className={styles.payCopy}>
              {guestMarkupPct
                ? t('payCopy.walletGuest', { pct: guestMarkupPct })
                : t('payCopy.walletDefault')}
            </p>
          )}
          {!gateBlocked && selected === 'wallet' && walletView?.selectable && credit && (
            <p className={styles.payCopy} data-make-good-credit={credit.id}>
              {t('payCopy.creditUsed', { amount: formatMoneyExplicit(credit.remainingCents, credit.currency, i18n.language) })}
            </p>
          )}
        </>
      )}

      {gateBlocked && preview && guestWindowBlocked && (
        <div className={styles.errorBanner}>
          <AlertTriangle size={14} />
          <div className={styles.errorBannerBody}>
            <p className={styles.errorBannerTitle}>
              {t('guestWindow.title')}
            </p>
            <p className={styles.errorBannerHint}>
              {preview.guestAccess?.nextOpenAt
                ? t('guestWindow.availableAgain', { when: formatRelativeFuture(new Date(preview.guestAccess.nextOpenAt)) })
                : t('guestWindow.hoursRestricted')}
            </p>
          </div>
        </div>
      )}

      {gateBlocked && preview && guestAccessDisabled && (
        <div className={styles.errorBanner}>
          <AlertTriangle size={14} />
          <div className={styles.errorBannerBody}>
            <p className={styles.errorBannerTitle}>
              {guestPriceNotSet ? t('guestNotOpenYet.title') : t('guestClosed.title')}
            </p>
            <p className={styles.errorBannerHint}>
              {guestPriceNotSet ? t('guestNotOpenYet.hint') : t('guestClosed.hint')}
            </p>
          </div>
        </div>
      )}

      {gateBlocked && preview && !guestWindowBlocked && !guestAccessDisabled && (
        <div className={styles.errorBanner}>
          <AlertTriangle size={14} />
          <div className={styles.errorBannerBody}>
            <p className={styles.errorBannerTitle}>
              {preview.reason === 'OUTSTANDING_BALANCE'
                ? t('gate.outstanding', { defaultValue: 'Outstanding balance' })
                : preview.reason === 'FLEET_SPONSORED_NO_CARD_PATH'
                ? t('gate.fleetSponsored')
                : preview.reason === 'PAYMENT_LOCKED'
                  ? t('gate.cardLocked')
                  : t('gate.walletLow')}
            </p>
            {preview.hint && (
              <p className={styles.errorBannerHint}>{preview.hint}</p>
            )}
            {/* Cross-tenant guests: a wallet top-up can never unblock
                them (the gate's cross-tenant guard), so the CTA
                either points at adding a card or — for a locked card,
                where nothing actionable exists — is omitted so it
                can't contradict the hint above it. */}
            {preview.reason === 'PAYMENT_LOCKED' && preview.accessMode === 'guest' ? null : (
              <button
                type="button"
                className={styles.errorBannerCta}
                onClick={() => onNavigate(
                  preview.reason === 'OUTSTANDING_BALANCE' || preview.reason === 'FLEET_SPONSORED_NO_CARD_PATH' || preview.accessMode === 'guest'
                    ? preview.reason === 'OUTSTANDING_BALANCE'
                      ? `/billing/payment-methods?return_to=${encodeURIComponent(window.location.pathname + window.location.search)}`
                      : '/billing/payment-methods'
                    // The top-up in the price's currency (a CAD wallet at a
                    // CAD charger), as the wallet option links it.
                    : walletView?.topUpPath ?? '/wallet',
                )}
              >
                {preview.reason === 'OUTSTANDING_BALANCE'
                  ? t('gate.payNow', { defaultValue: 'Pay now' })
                  : preview.reason === 'FLEET_SPONSORED_NO_CARD_PATH' || preview.accessMode === 'guest'
                  ? t('gate.addCard')
                  : preview.shortfallCents > 0
                    ? t('gate.topUpAmount', { amount: formatCents(preview.shortfallCents, walletView?.sessionCurrency ?? preview.wallet.currency, i18n.language) })
                    : t('gate.topUpWallet')}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Error surface — wallet gate, OCPP rejections, timeouts,
          and Fleet-Mode access denials (Phase 10).
          Rendered inline above the CTA so the driver sees it
          without scrolling.
          Phase 3D — `effectiveStartError` covers both the post-tap
          server-rejection path (setStartError) AND the proactive
          client-side prediction (proactiveAccessError). When the
          banner fires from a proactive prediction, onRetry calls
          handleStart directly — the mode switch has already
          changed the predicate result; no need to clear a stale
          startError (there isn't one). */}
      {effectiveStartError && (
        <StartErrorBanner
          error={effectiveStartError}
          onNavigate={onNavigate}
          onRetry={async () => {
            setStartError(null);
            await handleStart();
          }}
        />
      )}

      <div className={styles.ctaSpacer} />

      {recoveryId && <section className={styles.recoveryConfirmation}>
        <label><input type="checkbox" checked={recoveryConfirmedKey === `${chargerIdentity}:${activePlug}`}
          onChange={e => setRecoveryConfirmedKey(e.target.checked ? `${chargerIdentity}:${activePlug}` : '')} />
          {' '}{recoveryText(i18n.language, 3)}</label>
      </section>}
      <StickyCTA
        sticky={!recoveryId}
        disabled={
          !sessionInFlight && (
            (Boolean(recoveryId) && recoveryConfirmedKey !== `${chargerIdentity}:${activePlug}`)
            || gateBlocked
            || accessBlockedProactively
            || noPlugSelectable
            || waitingForPlug
            || (ctaState !== 'idle' && ctaState !== 'error')
          )
        }
        tone={ctaState === 'error' && !sessionInFlight ? 'danger' : 'accent'}
        onClick={
          sessionInFlight
            ? () => onNavigate('/')
            : gateBlocked || accessBlockedProactively || noPlugSelectable || waitingForPlug
              ? undefined
              : cardBlockedProactively
                ? () => onNavigate('/billing/payment-methods')
                : ctaState === 'error' || ctaState === 'idle'
                  ? () => void handleStart()
                  : undefined
        }
      >
        {sessionInFlight ? (
          <><Zap size={15} /> {t('cta.sessionInProgress')}</>
        ) : cardBlockedProactively && !gateBlocked && !noPlugSelectable && !waitingForPlug ? (
          // Expired start card: the CTA is the remedy, never Start.
          <><CreditCard size={15} /> {t('startError.addNewCard')}</>
        ) : ctaState === 'loading' ? (
          <><Loader2 size={16} className={styles.spin} /> {t('cta.sending')}</>
        ) : guestWindowBlocked && preview?.guestAccess?.nextOpenAt ? (
          <><Lock size={15} /> {t('cta.availableWhen', { when: formatRelativeFuture(new Date(preview.guestAccess.nextOpenAt)) })}</>
        ) : guestWindowBlocked ? (
          <><Lock size={15} /> {t('cta.guestClosed')}</>
        ) : guestAccessDisabled ? (
          <><Lock size={15} /> {guestPriceNotSet ? t('cta.guestNotOpenYet') : t('cta.guestClosed')}</>
        ) : gateBlocked ? (
          <><Lock size={15} /> {t('cta.topUpToStart')}</>
        ) : accessBlockedProactively ? (
          // Phase 3D — proactive access denial. CTA copy matches
          // the AccessDeniedBanner's title style; the banner above
          // carries the remediation button. The CTA itself stays
          // disabled rather than navigating, because the action
          // (mode switch / add card) lives in the banner.
          proactiveAccessError?.code === 'MUST_SWITCH_TO_FLEET' ? (
            <><Lock size={15} /> {t('cta.switchToFleet')}</>
          ) : proactiveAccessError?.code === 'MUST_SWITCH_TO_PUBLIC' ? (
            <><Lock size={15} /> {t('cta.switchToPublic')}</>
          ) : (
            <><Lock size={15} /> {t('cta.notAvailable')}</>
          )
        ) : ctaState === 'error' ? (
          <><AlertTriangle size={15} /> {t('cta.couldntStart')}</>
        ) : ctaState === 'offline' ? (
          <><WifiOff size={15} /> {t('cta.chargerOffline')}</>
        ) : ctaState === 'maint' ? (
          t('cta.inMaintenance')
        ) : ctaState === 'disabled-inactive' ? (
          t('cta.unavailable')
        ) : waitingForPlug ? (
          <>{t('cta.plugInToStart')}</>
        ) : noPlugSelectable ? (
          // Names the state of the plug the page settled on ("Plug 1 ·
          // Charging"), so the label reads as a fact, not a refusal.
          t('cta.plugBusy', { plug: activePlug, status: connectorStatusLabel(t, activeView) })
        ) : (
          <>
            <Zap size={15} />
            {' '}
            {isMultiPlug
              ? t('cta.startChargingPlug', { stall: charger.identity, plug: activePlug })
              : t('cta.startCharging', { stall: charger.identity })}
          </>
        )}
      </StickyCTA>
      {recoveryId && <div className={styles.recoveryClearance} aria-hidden="true" />}

      {/* Layer 2 — block-and-explain modal. Fires when handleStart
          sees no card on file; intercepts before any server call. */}
      {showAddPaymentModal && (
        <AddPaymentModal
          onCancel={() => setShowAddPaymentModal(false)}
          onConfirm={() => {
            setShowAddPaymentModal(false);
            onNavigate('/billing/payment-methods');
          }}
        />
      )}

      {/* Driver AI assistant — floating launcher for the global sheet.
          Hidden while the add-payment modal is open so it doesn't
          poke through the modal's overlay (modal is z-index 2000,
          FAB is 1500, but visual clutter under an obscured modal
          isn't ideal). The sheet itself lives in AppShell; context
          (charger id, support path) is bound via the effect above. */}
      <DriverAssistFab
        onClick={openEvie}
        hidden={showAddPaymentModal || evieOpen}
      />
    </div>
  );
}

// ── Add-payment-method modal (driver-friction fix Layer 2) ────────────────
//
// Renders when the driver taps "Start charging" without a payment
// method on file. Click-through to /billing/payment-methods or
// cancel to close. Inline-styled because the page already has its
// own CSS module and the modal is one-of-a-kind here — no shared
// modal primitive existed at the time of writing.
//
// Spec-mandated copy:
//   Title:   "Add payment to start charging"
//   Body:    "You'll need a payment method on file before you can
//             start a charge. We don't charge anything until your
//             session ends."
//   Primary: "Add payment method" → /billing/payment-methods
//   Secondary: "Cancel"

function AddPaymentModal({
  onCancel,
  onConfirm,
}: {
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation('preSession');
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-payment-modal-title"
      onClick={onCancel}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(380px, 100%)',
          background: 'var(--color-sf, #1a1a1a)',
          border: '1px solid var(--color-brd, var(--evi-border))',
          borderRadius: 12,
          padding: 20,
          boxShadow: '0 24px 64px rgba(0, 0, 0, 0.4)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <CreditCard size={22} style={{ color: 'var(--evi-accent, #2ECC71)' }} />
          <h2
            id="add-payment-modal-title"
            style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--color-tx)' }}
          >
            {t('addPayment.title')}
          </h2>
        </div>
        <p
          style={{
            margin: '0 0 18px',
            fontSize: 13,
            lineHeight: 1.5,
            color: 'var(--color-tx-m)',
          }}
        >
          {t('addPayment.body')}
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '9px 14px',
              background: 'transparent',
              color: 'var(--color-tx)',
              border: '1px solid var(--color-brd, var(--evi-border))',
              borderRadius: 8,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            {t('addPayment.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            style={{
              padding: '9px 14px',
              background: 'var(--evi-accent, #2ECC71)',
              color: '#000',
              border: 'none',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <CreditCard size={14} /> {t('addPayment.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Tariff row ────────────────────────────────────────────────────────────

function TariffRow({
  tariff, loading, connectionFeeCents,
}: {
  tariff?: PublicTariff;
  loading: boolean;
  /** start-preview's connectionFeeCents; undefined on a response that
   *  predates it. */
  connectionFeeCents?: number;
}) {
  const { t, i18n } = useTranslation('preSession');
  const [open, setOpen] = useState(false);

  if (loading) {
    return (
      <button type="button" className={styles.tariffRow} disabled>
        <div className={styles.tariffHeader}>
          <span className={styles.tariffLead}>{t('tariff.loading')}</span>
        </div>
      </button>
    );
  }
  if (!tariff) {
    return (
      <div className={styles.tariffRow}>
        <div className={styles.tariffHeader}>
          <span className={styles.tariffLead}>{t('tariff.noRate')}</span>
          <span className={styles.tariffDetail}>{t('tariff.noRateHint')}</span>
        </div>
      </div>
    );
  }

  const oneLiner = tariffOneLiner(tariff, i18n.language);

  return (
    <button
      type="button"
      className={styles.tariffRow}
      onClick={() => setOpen((v) => !v)}
      aria-expanded={open}
    >
      <div className={styles.tariffHeader}>
        <span className={styles.tariffLead}>{oneLiner}</span>
        <ChevronRight
          size={14}
          className={open ? styles.tariffChevOpen : styles.tariffChev}
        />
      </div>
      {open && (
        <div className={styles.tariffDetailBox}>
          <TariffDetail tariff={tariff} connectionFeeCents={connectionFeeCents} />
        </div>
      )}
    </button>
  );
}

// B.2: Tariff display dispatches on tariff.rate_type. The suffix
// (`/kWh` for energy billing, `/hour` for time-billed) follows
// from the unit; everything else (windows, tiers, peak changes,
// boundaries) is unit-agnostic. Defaults to 'per_kwh' for
// pre-Commit-D tariffs without rate_type.
function rateUnitSuffix(rateType: 'per_kwh' | 'per_hour'): string {
  return rateType === 'per_hour' ? '/hour' : '/kWh';
}
function tieredQuantityLabel(rateType: 'per_kwh' | 'per_hour', tierMax: number): string {
  return rateType === 'per_hour' ? `${tierMax}hr` : `${tierMax}kWh`;
}

function tariffOneLiner(tariff: PublicTariff, locale: string): string {
  const rateType = tariff.rate_type ?? 'per_kwh';
  const unit = rateUnitSuffix(rateType);
  // Every rate in the tariff's own currency (item 3).
  const fmt = (cents: number) => formatCents(cents, tariff.currency, locale);
  const base = `${fmt(tariff.rates.energy_rate)}${unit}`;
  if (tariff.type === 'flat') return base;
  if (tariff.type === 'time_of_use') {
    const current = findCurrentTouEntry(tariff);
    if (!current) return base;
    const currentRate = `${fmt(current.energy_rate)}${unit}`;
    const next = findNextTouBoundary(tariff);
    return next
      ? `${currentRate} · peak changes at ${formatHour(next)}`
      : currentRate;
  }
  if (tariff.type === 'tiered') {
    const first = tariff.rates.tiered_schedule?.[0];
    const second = tariff.rates.tiered_schedule?.[1];
    // Tier upper bound — read tier_max with fallback to legacy
    // up_to_kwh per the rename in 9f15f28.
    const firstMax = first?.tier_max ?? first?.up_to_kwh;
    if (first && second && firstMax != null) {
      return `${fmt(first.energy_rate)}${unit} up to ${tieredQuantityLabel(rateType, firstMax)}, then ${fmt(second.energy_rate)}`;
    }
    return base;
  }
  return base;
}

/**
 * The expanded rate list. EVI's connection fee (Tier 2) is a row of its
 * own with a note saying whose it is, and it counts toward rows.length:
 * "Flat rate. No other fees." must never show when a fee applies. The
 * tariff's own flat_fee is quoted only on a preview that predates
 * connectionFeeCents; a Tier 2 preview has rewritten it to EVI's fee,
 * which the connection-fee row already shows.
 */
function TariffDetail({
  tariff, connectionFeeCents,
}: {
  tariff: PublicTariff;
  connectionFeeCents?: number;
}) {
  const { t, i18n } = useTranslation('preSession');
  const rows: Array<{ label: string; value: React.ReactNode }> = [];
  const rateType = tariff.rate_type ?? 'per_kwh';
  const unit = rateUnitSuffix(rateType);
  // Every row in the tariff's own currency (item 3).
  const fmt = (cents: number) => formatCents(cents, tariff.currency, i18n.language);
  if (tariff.type === 'time_of_use' && tariff.rates.time_of_use_schedule) {
    for (const win of tariff.rates.time_of_use_schedule) {
      const days = formatDays(win.day_of_week, t, i18n.language);
      rows.push({
        label: `${days} ${formatHour(win.start_hour)}–${formatHour(win.end_hour)}`,
        value: `${fmt(win.energy_rate)}${unit}`,
      });
    }
  }
  if (tariff.type === 'tiered' && tariff.rates.tiered_schedule) {
    let prev = 0;
    for (const tier of tariff.rates.tiered_schedule) {
      const tierMaxValue = tier.tier_max ?? tier.up_to_kwh;
      const upto = tierMaxValue == null ? '∞' : tieredQuantityLabel(rateType, tierMaxValue);
      const prevLabel = rateType === 'per_hour' ? `${prev}hr` : `${prev}`;
      rows.push({
        label: `${prevLabel}–${upto}`,
        value: `${fmt(tier.energy_rate)}${unit}`,
      });
      if (tierMaxValue != null) prev = tierMaxValue;
    }
  }
  if (tariff.rates.time_rate && rateType !== 'per_hour') {
    // Billed after the grace, for at most IDLE_FEE_MAX_MINUTES a session.
    // A per-hour tariff bills the whole stay and never bills idle.
    rows.push({
      label: t('tariff.idleFee'),
      value: renderInline(idleDisclosure(tariff.rates, tariff.currency, i18n.language, t)),
    });
  }
  // The tariff's own rates.flat_fee is retired (Tier 2): it never bills a
  // new session, so it is never listed. EVI's connection fee comes from the
  // start-preview only.
  const showConnectionFee = (connectionFeeCents ?? 0) > 0;
  if (showConnectionFee) {
    rows.push({ label: t('tariff.connectionFee'), value: fmt(connectionFeeCents ?? 0) });
  }
  if (tariff.tax_rate) {
    rows.push({ label: t('tariff.tax'), value: `${(tariff.tax_rate * 100).toFixed(1)}%` });
  }

  if (rows.length === 0) {
    // "No other fees" only when the preview has said so. With no preview
    // yet the fee is unknown, and the claim could be false.
    return connectionFeeCents === undefined
      ? null
      : <p className={styles.tariffDetail}>{t('tariff.flatRate')}</p>;
  }
  return (
    <>
      <dl className={styles.tariffList}>
        {rows.map((r, i) => (
          <div key={i} className={styles.tariffListRow}>
            <dt>{r.label}</dt>
            <dd>{r.value}</dd>
          </div>
        ))}
      </dl>
      {showConnectionFee && (
        <p className={styles.tariffDetail}>{t('tariff.connectionFeeNote')}</p>
      )}
    </>
  );
}

type TouEntry = NonNullable<PublicTariff['rates']['time_of_use_schedule']>[number];

function findCurrentTouEntry(tariff: PublicTariff): TouEntry | undefined {
  const schedule = tariff.rates.time_of_use_schedule;
  if (!schedule || schedule.length === 0) return undefined;
  const now = new Date();
  const day = now.getDay();
  const hour = now.getHours();
  return schedule.find(
    (w) => w.day_of_week.includes(day) && hour >= w.start_hour && hour < w.end_hour,
  );
}

function findNextTouBoundary(tariff: PublicTariff): number | null {
  const schedule = tariff.rates.time_of_use_schedule;
  if (!schedule || schedule.length === 0) return null;
  const now = new Date();
  const day = now.getDay();
  const hour = now.getHours();
  // Find the earliest boundary later today.
  let soonest: number | null = null;
  for (const w of schedule) {
    if (!w.day_of_week.includes(day)) continue;
    for (const boundary of [w.start_hour, w.end_hour]) {
      if (boundary > hour) {
        if (soonest === null || boundary < soonest) soonest = boundary;
      }
    }
  }
  return soonest;
}

// ── Error banner ──────────────────────────────────────────────────────────

// Reason codes Phase 2's resolveSessionAccess emits + Phase 7's
// FLEET_PAYMENT_METHOD_REQUIRED. The server wraps them in
// { error: 'SESSION_ACCESS_DENIED', code: <reason> } so we narrow by
// `code` here. All other codes (wallet, offline, timeout, generic)
// fall through to the legacy banner shape below.
const SESSION_ACCESS_REASONS = new Set<SessionAccessReason>([
  'MUST_SWITCH_TO_PUBLIC',
  'MUST_SWITCH_TO_FLEET',
  'NOT_IN_THIS_FLEET',
  'DIFFERENT_FLEET',
  'FLEET_PAYMENT_METHOD_REQUIRED',
  'NOT_IN_GROUP',
]);

function isAccessDeniedCode(code: string): code is SessionAccessReason {
  return SESSION_ACCESS_REASONS.has(code as SessionAccessReason);
}

function StartErrorBanner({
  error, onNavigate, onRetry,
}: {
  error: DriverStartError;
  onNavigate: (path: string) => void;
  onRetry: () => Promise<void> | void;
}) {
  const { t, i18n } = useTranslation('preSession');
  if (error.code === 'RECOVERY_UNSAFE' || error.code === 'RECOVERY_CONFIRM_REQUIRED') return (
    <div role="alert" className={styles.errorBanner}>{recoveryText(i18n.language, error.code === 'RECOVERY_CONFIRM_REQUIRED' ? 4 : 5)}</div>
  );
  // Phase 10 — Fleet-Mode access-denied codes get their own card
  // shape with a single-tap remediation action where one exists.
  // Inline rather than a separate component because the props it
  // would take are exactly the parent state (error + onNavigate +
  // onRetry); a sub-component would just shuffle the same args.
  if (isAccessDeniedCode(error.code)) {
    return (
      <AccessDeniedBanner
        reason={error.code}
        message={error.hint ?? error.error ?? t('startError.cannotStart')}
        onNavigate={onNavigate}
        onRetry={onRetry}
      />
    );
  }

  const isWalletGate = error.code === 'WALLET_GATE_FAILED' || error.code === 'INSUFFICIENT_BALANCE';
  const isOffline = error.code === 'CHARGER_OFFLINE';
  const isTimeout = error.code === 'OCPP_TIMEOUT';
  // driver-start's 400 for a connector_id that is not on this charger.
  // The page has already re-resolved the plug and scrolled the switcher
  // into view; this copy is the localized version of the server's own
  // hint, so the raw hint is suppressed rather than printed twice.
  const isInvalidConnector = error.code === 'INVALID_CONNECTOR';
  // Item 6: driver-start's unpriced refusal. Its English hint is replaced
  // by the localized one.
  const priceNotSet = isPriceNotSet(error);
  const cardExpired = isExpiredStartCard(error);
  // A v1-era saved card Stripe will not reuse (the SPECK case, 29 Oct 2026):
  // add it again, with a link, instead of the generic failure.
  const cardReadd = !cardExpired && isReaddStartCard(error);
  // The private switch: driver-start's 403 PRIVATE_CHARGER, localized.
  const privateCharger = isPrivateChargerError(error);

  return (
    <div className={styles.errorBanner}>
      <AlertTriangle size={14} />
      <div className={styles.errorBannerBody}>
        <p className={styles.errorBannerTitle}>
          {privateCharger
            ? t('privateCharger.title')
            : cardExpired
            ? t('startError.cardExpired')
            : cardReadd
            ? t('startError.cardReadd')
            : priceNotSet
            ? t('guestNotOpenYet.title')
            : isInvalidConnector
            ? t('error.invalidConnector')
            : isWalletGate
              ? t('startError.walletGate')
              : isOffline
                ? t('startError.offline')
                : isTimeout
                  ? t('startError.timeout')
                  : t('startError.generic')}
        </p>
        {privateCharger
          ? <p className={styles.errorBannerHint}>{t('privateCharger.hint')}</p>
          : cardExpired
          ? <p className={styles.errorBannerHint}>{t('startError.cardExpiredHint')}</p>
          : cardReadd
          ? <p className={styles.errorBannerHint}>{t('startError.cardReaddHint')}</p>
          : priceNotSet
          ? <p className={styles.errorBannerHint}>{t('guestNotOpenYet.hint')}</p>
          : error.hint && !isInvalidConnector && <p className={styles.errorBannerHint}>{error.hint}</p>}
        {cardExpired && (
          <button type="button" className={styles.errorBannerCta} onClick={() => onNavigate('/billing/payment-methods')}>
            {t('startError.addNewCard')}
          </button>
        )}
        {cardReadd && (
          <button type="button" className={styles.errorBannerCta} onClick={() => onNavigate(error.data?.readdCardPath ?? '/billing/payment-methods')}>
            {t('startError.cardReaddCta')}
          </button>
        )}
        {isWalletGate && error.data?.shortfallCents != null && (
          <button
            type="button"
            className={styles.errorBannerCta}
            onClick={() => onNavigate('/wallet')}
          >
            {t('startError.addAmount', { amount: formatCents(error.data.shortfallCents) })}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Phase 10 — inline blocked-card UI for SESSION_ACCESS_DENIED rejections.
 *
 * Replaces the prior toast-only UX for Phase 2 / Phase 7 gate codes.
 * Each reason maps to a per-row copy + a single remediation action:
 *
 *   MUST_SWITCH_TO_FLEET           → "Switch to Fleet Mode"      → toggleMode + retry
 *   MUST_SWITCH_TO_PUBLIC          → "Switch to Public Charging" → toggleMode + retry
 *   NOT_IN_THIS_FLEET              → informational, no action
 *   DIFFERENT_FLEET                → informational, no action
 *   FLEET_PAYMENT_METHOD_REQUIRED  → "Add Payment Method"        → /billing/payment-methods
 *
 * Visual treatment mirrors the existing `.errorBanner` shape
 * (AlertTriangle + title + hint + optional CTA), same precedent as
 * the OUTSIDE_GUEST_WINDOW banner. Stays inline above the Sticky
 * CTA so the driver doesn't have to scroll.
 *
 * After a successful mode switch, `onRetry` re-fires the start
 * attempt. If the retry hits another rejection (e.g., they switched
 * to fleet mode but the fleet requires a card they don't have),
 * the new code lands back in this banner on the same render — no
 * recursion, no double-toast.
 */
function AccessDeniedBanner({
  reason, message, onNavigate, onRetry,
}: {
  reason: SessionAccessReason;
  message: string;
  onNavigate: (path: string) => void;
  onRetry: () => Promise<void> | void;
}) {
  const { t } = useTranslation('preSession');
  const { toggle: toggleMode, isSwitching } = useCurrentMode();
  const [retrying, setRetrying] = useState(false);

  const isModeSwitchable =
    reason === 'MUST_SWITCH_TO_FLEET' || reason === 'MUST_SWITCH_TO_PUBLIC';
  const needsPaymentMethod = reason === 'FLEET_PAYMENT_METHOD_REQUIRED';

  const title = (() => {
    switch (reason) {
      case 'MUST_SWITCH_TO_FLEET':
        return t('access.fleetChargerTitle');
      case 'MUST_SWITCH_TO_PUBLIC':
        return t('access.inFleetModeTitle');
      case 'NOT_IN_THIS_FLEET':
        return t('access.notInFleetTitle');
      case 'DIFFERENT_FLEET':
        return t('access.differentFleetTitle');
      case 'FLEET_PAYMENT_METHOD_REQUIRED':
        return t('access.addPaymentTitle');
      case 'NOT_IN_GROUP':
        return t('access.notInGroupTitle');
    }
  })();

  const ctaLabel = (() => {
    if (reason === 'MUST_SWITCH_TO_FLEET') return t('access.switchToFleet');
    if (reason === 'MUST_SWITCH_TO_PUBLIC') return t('access.switchToPublic');
    if (needsPaymentMethod) return t('access.addPaymentMethod');
    return null;
  })();

  const handleAction = async (): Promise<void> => {
    if (needsPaymentMethod) {
      onNavigate('/billing/payment-methods');
      return;
    }
    if (!isModeSwitchable) return;
    setRetrying(true);
    try {
      await toggleMode();
      // toggleMode rolls back the auth-store on rejection and
      // surfaces its own toast; if we landed here without throwing,
      // the mode flipped successfully. Retry the session-start.
      await onRetry();
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div className={styles.errorBanner}>
      <AlertTriangle size={14} />
      <div className={styles.errorBannerBody}>
        <p className={styles.errorBannerTitle}>{title}</p>
        <p className={styles.errorBannerHint}>{message}</p>
        {ctaLabel && (
          <button
            type="button"
            className={styles.errorBannerCta}
            onClick={() => { void handleAction(); }}
            disabled={isSwitching || retrying}
          >
            {retrying ? t('access.switching') : ctaLabel}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Formatting helpers ────────────────────────────────────────────────────

/** Render a future Date as a human-friendly relative string for the
 *  OUTSIDE_GUEST_WINDOW banner + CTA. Mirrors the cadence of
 *  PaymentSelector's `formatRelativeFuture` (which the third-party-
 *  charger-access ticket also added, in the locked-card disabled
 *  copy) — same shape, fits with copy "in 3 hours" / "tomorrow at
 *  9 AM" / "Tue at 9 AM" etc. Inline rather than a shared util
 *  because date-fns isn't in the PWA's deps and the formatter is
 *  tiny. Lift if a third copy point appears. */
function formatRelativeFuture(date: Date): string {
  const diffMs = date.getTime() - Date.now();
  if (diffMs <= 0) return 'now';
  const hours = Math.round(diffMs / (60 * 60 * 1000));
  if (hours < 24) {
    if (hours <= 1) return 'in less than 1 hour';
    return `in ${hours} hours`;
  }
  const days = Math.floor(hours / 24);
  const timeStr = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  if (days === 1) return `tomorrow at ${timeStr}`;
  if (days < 7) {
    return `${date.toLocaleDateString(undefined, { weekday: 'short' })} at ${timeStr}`;
  }
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) + ` at ${timeStr}`;
}

/** An amount in its own currency (item 3): the tariff's for rates, fees
 *  and the hold, the wallet's for a balance. "45¢" under a dollar only
 *  where that cannot be mistaken (USD in English); "CA$0.45" otherwise.
 *  No currency reads USD, what every amount was before. */
function formatCents(cents: number | undefined, currency?: string | null, locale?: string | null): string {
  if (cents == null) return '—';
  return formatMoneyCompact(cents, currency, locale);
}

function formatHour(hour: number): string {
  const h = hour % 24;
  if (h === 0) return '12am';
  if (h === 12) return '12pm';
  if (h < 12) return `${h}am`;
  return `${h - 12}pm`;
}

function formatDays(days: number[], t: TFunction, locale: string): string {
  if (days.length === 7) return t('daySummary.everyDay');
  const weekdays = [1, 2, 3, 4, 5];
  const weekend = [0, 6];
  const isWeekdays = weekdays.every((d) => days.includes(d)) && !days.includes(0) && !days.includes(6);
  const isWeekend = weekend.every((d) => days.includes(d)) && days.length === 2;
  if (isWeekdays) return t('daySummary.weekdays');
  if (isWeekend) return t('daySummary.weekends');
  return days.map((d) => weekdayShort(d, locale)).filter(Boolean).join('/');
}

function formatPaymentMethod(
  methods: { default_payment_method: string | null; payment_methods: Array<{ id: string; brand?: string; last4?: string }> } | undefined,
): string {
  if (!methods) return '—';
  const defaultId = methods.default_payment_method;
  const pm = methods.payment_methods.find((m) => m.id === defaultId);
  if (!pm) return 'Card on file';
  return `${pm.brand ?? 'Card'} •••• ${pm.last4 ?? '—'}`;
}

// Initial selector source. Mirrors gate.ts so the UI shows the same
// path the gate would resolve to on tap. Honors stored preference
// when actionable, otherwise prefers card (less friction — no top-up
// required), then wallet, else null (Start is disabled regardless).
function pickInitialSource(p: StartPreviewResponse, cardExpired = false, creditOnWallet = false): 'card' | 'wallet' | null {
  // An expired start card is not a usable default: preselect a sufficient
  // wallet instead. With no wallet the card stays selected and blocked.
  const cardOk = p.card.available && !cardExpired;
  // Wallet is only pickable when the GATE could resolve to it: the
  // preview carries the gate's own wallet option (walletOptionView), so a
  // guest, a fleet session or a wallet in another currency never is.
  const walletOk = walletOptionView(p).selectable;
  // An unspent make-good credit in the wallet that would pay opens on
  // Wallet, whatever the stored choice (Warren, 2026-10-28).
  if (creditOnWallet && walletOk) return 'wallet';
  if (p.preferredPaymentSource === 'card' && cardOk) return 'card';
  if (p.preferredPaymentSource === 'wallet' && walletOk) return 'wallet';
  if (cardOk) return 'card';
  if (walletOk) return 'wallet';
  return null;
}
